/**
 * Edit Preview List Component
 *
 * Simple container component that renders AutoRedlinePreview components
 * Each preview manages its own state independently
 */

import React, { useRef, useEffect, useState, useCallback } from "react";
import { AutoRedlineOutput } from "../../interfaces/autoRedlineTypes";
import { AutoRedlinePreview, AutoRedlinePreviewHandle } from "./AutoRedlinePreview";
import { ComposerModeIcon } from "./ComposerModeIcon";
import { NumberingChangeIndicator } from "./NumberingChangeIndicator";
import { createLogger } from "../../utils/logger";
import { sanitizeRedlineHtml } from "../../utils/sanitizeHtml";

const log = createLogger("EditPreviewList");
import {
  listContainerStyle,
  listHeaderStyle,
  composerModeIndicatorStyle,
  emptyStateStyle,
  numberedPreviewRowStyle,
  numberingColumnStyle,
  numberedPreviewTextStyle,
} from "./styles/editPreviewStyles";

interface EditPreviewListProps {
  /** Array of auto-redline outputs to display */
  autoRedlines: AutoRedlineOutput[];

  /** Whether composer mode is enabled (for UI display only) */
  composerMode: boolean;

  /** Whether composer mode was active when this message was created */
  wasComposerModeActiveOnCreation?: boolean;

  /** Whether this message came back from a stored conversation */
  wasRestoredFromPersistence?: boolean;

  /** Whether the message is still streaming */
  isStreaming?: boolean;

  /** Whether this is a read-only view */
  isReadOnly?: boolean;

  /** Message ID for tracking auto-apply state */
  messageId: string;

  /** Callback to update message state */
  onUpdateMessage: (
    messageId: string,
    updates: { autoRedlineApplied?: boolean; autoRedlineApplyIndex?: number }
  ) => void;

  /** Whether auto-redline has already been applied (from message state) */
  hasAlreadyApplied?: boolean;

  /** This list's position among auto-redline lists (0-based) */
  autoApplyIndex?: number;

  /** Total number of auto-redline lists in this message */
  totalAutoApplyLists?: number;

  /** Which list's turn it is to auto-apply (from message state) */
  currentApplyTurn?: number;
}

/**
 * Container component for rendering multiple edit previews
 * In composer mode, automatically triggers apply for each preview
 */
export const EditPreviewList: React.FC<EditPreviewListProps> = ({
  autoRedlines,
  composerMode,
  wasComposerModeActiveOnCreation = false,
  wasRestoredFromPersistence = false,
  isStreaming = false,
  isReadOnly = false,
  messageId,
  onUpdateMessage,
  hasAlreadyApplied = false,
  autoApplyIndex = 0,
  totalAutoApplyLists = 1,
  currentApplyTurn,
}) => {
  // Refs to all preview components for programmatic control
  const previewRefs = useRef<(AutoRedlinePreviewHandle | null)[]>([]);
  const [isAutoApplying, setIsAutoApplying] = useState(false);
  const [currentApplyingIndex, setCurrentApplyingIndex] = useState<number | null>(null);
  const [autoApplyCountdown, setAutoApplyCountdown] = useState<number | null>(null);
  const countdownTimerRef = useRef<NodeJS.Timeout | null>(null);
  const hasTriggeredRef = useRef(false);

  // Calculate total edit count
  const totalEditCount = React.useMemo(() => {
    return autoRedlines.reduce((sum, redline) => sum + redline.edits.length, 0);
  }, [autoRedlines]);

  // Pass turn immediately if this list has 0 edits and it's our turn in composer mode
  const isMyTurn = (currentApplyTurn ?? 0) === autoApplyIndex;
  useEffect(() => {
    if (
      isMyTurn &&
      totalEditCount === 0 &&
      wasComposerModeActiveOnCreation &&
      !wasRestoredFromPersistence &&
      !isStreaming &&
      !hasAlreadyApplied
    ) {
      const isLastList = autoApplyIndex === totalAutoApplyLists - 1;
      if (isLastList) {
        onUpdateMessage(messageId, { autoRedlineApplied: true });
      } else {
        onUpdateMessage(messageId, { autoRedlineApplyIndex: autoApplyIndex + 1 });
      }
    }
  }, [
    isMyTurn,
    totalEditCount,
    wasComposerModeActiveOnCreation,
    wasRestoredFromPersistence,
    isStreaming,
    hasAlreadyApplied,
    autoApplyIndex,
    totalAutoApplyLists,
    messageId,
    onUpdateMessage,
  ]);

  // Auto-apply logic for composer mode
  const triggerAutoApply = useCallback(async () => {
    if (isAutoApplying || isReadOnly || totalEditCount === 0) return;

    log.info("Starting composer mode auto-apply sequence", {
      previewCount: autoRedlines.length,
      totalEdits: totalEditCount,
      messageId,
    });

    setIsAutoApplying(true);
    hasTriggeredRef.current = true;

    // Apply each preview sequentially
    for (let i = 0; i < previewRefs.current.length; i++) {
      const ref = previewRefs.current[i];
      if (!ref) continue;

      // Check if this preview is already applying or complete
      if (ref.isApplying()) {
        log.info(`Preview ${i} is already applying, skipping`);
        continue;
      }

      setCurrentApplyingIndex(i);
      log.info(`Auto-applying preview ${i + 1}/${autoRedlines.length}`);

      try {
        await ref.applyAll();
        log.info(`Preview ${i + 1} completed`);
      } catch (error) {
        log.error(`Failed to auto-apply preview ${i + 1}`, error);
      }

      // Add delay between previews for visibility
      if (i < previewRefs.current.length - 1) {
        await new Promise((resolve) => setTimeout(resolve, 1000));
      }
    }

    setIsAutoApplying(false);
    setCurrentApplyingIndex(null);

    // Signal completion: either pass turn to next list or mark all as applied
    const isLastList = autoApplyIndex === totalAutoApplyLists - 1;
    if (isLastList) {
      onUpdateMessage(messageId, { autoRedlineApplied: true });
      log.info("All auto-redline lists applied", { messageId });
    } else {
      onUpdateMessage(messageId, { autoRedlineApplyIndex: autoApplyIndex + 1 });
      log.info("Passing turn to next auto-redline list", {
        messageId,
        nextIndex: autoApplyIndex + 1,
      });
    }
  }, [
    isAutoApplying,
    isReadOnly,
    totalEditCount,
    autoRedlines.length,
    messageId,
    onUpdateMessage,
    autoApplyIndex,
    totalAutoApplyLists,
  ]);

  // Start countdown when message was created with composer mode and streaming stops
  useEffect(() => {
    // A restored message never starts the countdown. hasTriggeredRef is
    // component-local, so a conversation reopened before its edits were applied
    // would otherwise count down unattended and write into whatever document is
    // open now, which may not be the one the suggestions were made against. The
    // cards stay live: applying them manually still goes through the usual
    // stale-paragraph guard.
    if (wasRestoredFromPersistence) {
      return;
    }

    if (
      wasComposerModeActiveOnCreation &&
      !isStreaming &&
      !isReadOnly &&
      totalEditCount > 0 &&
      !hasTriggeredRef.current &&
      !hasAlreadyApplied &&
      isMyTurn
    ) {
      // First list: full 3-second countdown; subsequent lists: apply immediately
      const countdownStart = autoApplyIndex === 0 ? 3 : 0;

      if (countdownStart === 0) {
        // No countdown — apply immediately
        triggerAutoApply();
        return;
      }

      // Start countdown
      let countdown = countdownStart;
      setAutoApplyCountdown(countdown);

      countdownTimerRef.current = setInterval(() => {
        countdown--;
        if (countdown > 0) {
          setAutoApplyCountdown(countdown);
        } else {
          // Clear countdown and trigger auto-apply
          if (countdownTimerRef.current) {
            clearInterval(countdownTimerRef.current);
            countdownTimerRef.current = null;
          }
          setAutoApplyCountdown(null);
          triggerAutoApply();
        }
      }, 1000);

      return () => {
        if (countdownTimerRef.current) {
          clearInterval(countdownTimerRef.current);
          countdownTimerRef.current = null;
        }
      };
    }
  }, [
    wasComposerModeActiveOnCreation,
    wasRestoredFromPersistence,
    isStreaming,
    isReadOnly,
    totalEditCount,
    triggerAutoApply,
    hasAlreadyApplied,
    isMyTurn,
    autoApplyIndex,
  ]);

  // Reset triggered flag when conditions change
  useEffect(() => {
    if (!wasComposerModeActiveOnCreation || isStreaming) {
      hasTriggeredRef.current = false;
      setAutoApplyCountdown(null);
      if (countdownTimerRef.current) {
        clearInterval(countdownTimerRef.current);
        countdownTimerRef.current = null;
      }
    }
  }, [wasComposerModeActiveOnCreation, isStreaming]);

  // Don't render if no autoRedlines
  if (autoRedlines.length === 0) {
    return null;
  }

  // If all redlines have no edits, still show the preview
  if (totalEditCount === 0) {
    return (
      <div style={listContainerStyle}>
        <div style={listHeaderStyle}>Suggested Changes (Preview Only)</div>
        {autoRedlines.map((redline, index) => (
          <div key={`preview-${index}`} style={{ padding: "12px" }}>
            {redline.sectionName && (
              <div style={{ fontWeight: 600, marginBottom: "8px" }}>{redline.sectionName}</div>
            )}
            {/* Same gutter precedence as AutoRedlinePreview:
                (1) numberingChange → Word-Ribbon-style Fluent icon + tooltip;
                (2) numberingPreview → native-numbering HTML gutter;
                (3) neither → single-column layout (no empty gutter). */}
            <div
              style={{
                fontSize: "14px",
                lineHeight: "1.6",
                backgroundColor: "#f5f5f5",
                padding: "12px",
                borderRadius: "4px",
              }}
            >
              {redline.numberingChange && typeof redline.numberingChange === "object" ? (
                <div style={numberedPreviewRowStyle}>
                  <div style={numberingColumnStyle}>
                    <NumberingChangeIndicator change={redline.numberingChange} />
                  </div>
                  <div
                    style={numberedPreviewTextStyle}
                    dangerouslySetInnerHTML={{ __html: sanitizeRedlineHtml(redline.previewText) }}
                  />
                </div>
              ) : typeof redline.numberingPreview === "string" &&
                redline.numberingPreview.length > 0 ? (
                <div style={numberedPreviewRowStyle}>
                  <div
                    style={numberingColumnStyle}
                    dangerouslySetInnerHTML={{
                      __html: sanitizeRedlineHtml(redline.numberingPreview),
                    }}
                  />
                  <div
                    style={numberedPreviewTextStyle}
                    dangerouslySetInnerHTML={{ __html: sanitizeRedlineHtml(redline.previewText) }}
                  />
                </div>
              ) : (
                <div
                  dangerouslySetInnerHTML={{ __html: sanitizeRedlineHtml(redline.previewText) }}
                />
              )}
            </div>
          </div>
        ))}
        <div style={emptyStateStyle}>No edits to apply - preview only</div>
      </div>
    );
  }

  return (
    <div style={listContainerStyle}>
      {/* Header */}
      <div style={listHeaderStyle}>
        <span>Suggested Changes</span>
        {wasComposerModeActiveOnCreation && !wasRestoredFromPersistence && !isReadOnly && (
          <span style={composerModeIndicatorStyle}>
            <ComposerModeIcon size={16} />
            <span>
              Composer Mode
              {autoApplyCountdown !== null && ` (Auto-applying in ${autoApplyCountdown}...)`}
              {isAutoApplying &&
                currentApplyingIndex !== null &&
                ` (Applying ${currentApplyingIndex + 1}/${autoRedlines.length})`}
            </span>
          </span>
        )}
      </div>

      {/* Auto-Redline Previews - Each manages its own state */}
      {autoRedlines.map((redline, index) => (
        <AutoRedlinePreview
          key={`redline-${index}`}
          ref={(el) => {
            previewRefs.current[index] = el;
          }}
          redline={redline}
          // Composer mode replaces the Apply button with a disabled
          // "auto-apply enabled" pill, which is only honest while the countdown
          // can still run. On a restored message it never will, so the card has
          // to fall back to a normal, clickable Apply.
          composerMode={wasComposerModeActiveOnCreation && !wasRestoredFromPersistence}
          isReadOnly={isReadOnly}
          isStreaming={isStreaming}
          messageId={messageId}
        />
      ))}
    </div>
  );
};
